import React, { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supportTicketService } from '../../api/services/supportTicketService';

interface Props {
  orderId: number;
  orderNumber: string | number;
  onClose: () => void;
}

const CreateSupportTicketModal: React.FC<Props> = ({ orderId, orderNumber, onClose }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', comment: '' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<typeof form>>({});
  const [savedTicketId, setSavedTicketId] = useState<number | null>(null);

  const validate = () => {
    const next: Partial<typeof form> = {};
    if (!form.name.trim()) next.name = 'Required';
    if (!form.phone.trim()) next.phone = 'Required';
    if (!form.email.trim()) next.email = 'Required';
    if (!form.comment.trim()) next.comment = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof form]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const result = await supportTicketService.createSupportTicket({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        comment: form.comment.trim(),
        orderId,
      });
      setSavedTicketId(result.id);
    } catch {
      toast.error('Failed to create support ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add support ticket</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={20} />
          </button>
        </div>

        {savedTicketId !== null ? (
          /* Success view */
          <>
            <div className="px-6 py-8 flex flex-col items-center gap-6">
              <div className="w-full bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded px-4 py-3 text-sm text-green-800 dark:text-green-300">
                Thank you for calling our Support Team will be giving you a call, here's your Ticket #:
              </div>
              <p className="text-4xl font-bold text-green-600 dark:text-green-400">{savedTicketId}</p>
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm rounded hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          /* Form view */
          <>
            <div className="px-6 py-5 space-y-4">
              <div className="pb-2 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Ticket</h3>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400">
                Linked to Sales Order #{orderNumber}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  maxLength={50}
                  className={`w-full px-3 py-2 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
                    focus:outline-none focus:ring-1 focus:ring-blue-500`}
                />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Phone <span className="text-red-500">*</span>
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  maxLength={50}
                  className={`w-full px-3 py-2 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    ${errors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
                    focus:outline-none focus:ring-1 focus:ring-blue-500`}
                />
                {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  maxLength={50}
                  className={`w-full px-3 py-2 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    ${errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
                    focus:outline-none focus:ring-1 focus:ring-blue-500`}
                />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Comment <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="comment"
                  value={form.comment}
                  onChange={handleChange}
                  maxLength={1023}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none
                    ${errors.comment ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
                    focus:outline-none focus:ring-1 focus:ring-blue-500`}
                />
                {errors.comment && <p className="mt-1 text-xs text-red-500">{errors.comment}</p>}
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm rounded hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateSupportTicketModal;
